import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Container, Input } from 'reactstrap';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import { unwrapPayload } from '../../utilities';

type ExclusionsSectionProperties =
{
    getCategoryExclusions: () => Promise<any>
    updateCategoryExclusions: (values: any) => Promise<any>
}

type CategoryItem =
{
    categoryId: number
    parentCategoryId?: number | null
    name?: string | null
    path?: string | null
    level?: number | null
    directProductCount?: number | null
    isExcluded?: boolean
    isInheritedExcluded?: boolean
}

function normalizeCategoryIds(categoryIds: any): number[]
{
    const ids: number[] = (categoryIds ?? [])
            .map((categoryId: any) => Number.parseInt(categoryId, 10))
            .filter((categoryId: number) => Number.isFinite(categoryId) && categoryId > 0);
    return Array.from(new Set(ids)).sort((a, b) => a - b);
}

function toTreeKey(categoryId: number): string
{
    return String(categoryId);
}

function toCategoryId(treeKey: React.Key): number
{
    return Number.parseInt(String(treeKey), 10);
}

function getEffectiveExcludedCategoryIds(payload: any): number[]
{
    const categories = payload?.categories ?? [];
    const effectiveCategoryIds = categories
        .filter((category: CategoryItem) => category.isExcluded || category.isInheritedExcluded)
        .map((category: CategoryItem) => category.categoryId);

    if (effectiveCategoryIds.length > 0)
    {
        return normalizeCategoryIds(effectiveCategoryIds);
    }

    return normalizeCategoryIds(payload?.settings?.excludedCategoryIds);
}

function escapeRegExp(value: string): string
{
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightText(value: string, searchPhrase: string)
{
    const phrase = searchPhrase.trim();

    if (!phrase)
    {
        return value;
    }

    const parts = value.split(new RegExp(`(${escapeRegExp(phrase)})`, 'ig'));

    return parts.map((part, index) =>
        part.toLowerCase() === phrase.toLowerCase()
            ? <mark key={index} className="px-0">{part}</mark>
            : <React.Fragment key={index}>{part}</React.Fragment>);
}

function ExclusionsSection
(
    {
        getCategoryExclusions,
        updateCategoryExclusions
    }
    : ExclusionsSectionProperties
)
{
    const [data, setData] = useState<any | null>(null);
    const [initialExcludedCategoryIds, setInitialExcludedCategoryIds] = useState<number[]>([]);
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchPhrase, setSearchPhrase] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lastError, setLastError] = useState<string | null>(null);
    const [lastMessage, setLastMessage] = useState<string | null>(null);

    const loadData = async () =>
    {
        setLastError(null);
        setIsLoading(true);

        try
        {
            const result = await getCategoryExclusions();
            const payload = unwrapPayload(result);
            const effectiveExcludedCategoryIds = getEffectiveExcludedCategoryIds(payload);

            setData(payload);
            setInitialExcludedCategoryIds(effectiveExcludedCategoryIds);
            setCheckedKeys(effectiveExcludedCategoryIds.map(toTreeKey));
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Pobieranie wykluczeń kategorii zakończyło się błędem.');
        }
        finally
        {
            setIsLoading(false);
        }
    };

    useEffect(() =>
    {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const categories: CategoryItem[] = useMemo
    (
        () => data?.categories ?? [],
        [data]
    );

    const categoriesById = useMemo(() =>
    {
        const result = new Map<number, CategoryItem>();

        categories.forEach(category =>
        {
            result.set(category.categoryId, category);
        });

        return result;
    }, [categories]);

    const parentIdByCategoryId = useMemo(() =>
    {
        const result = new Map<number, number | null>();

        categories.forEach(category =>
        {
            result.set(category.categoryId, category.parentCategoryId ?? null);
        });

        return result;
    }, [categories]);

    const matchingCategoryIds = useMemo(() =>
    {
        const phrase = searchPhrase.trim().toLowerCase();
        const result = new Set<number>();

        if (!phrase)
        {
            return result;
        }

        categories.forEach(category =>
        {
            if (
                String(category.categoryId).includes(phrase) ||
                (category.name ?? '').toLowerCase().includes(phrase) ||
                (category.path ?? '').toLowerCase().includes(phrase)
            )
            {
                result.add(category.categoryId);
            }
        });

        return result;
    }, [categories, searchPhrase]);

    useEffect(() =>
    {
        if (!searchPhrase.trim())
        {
            return;
        }

        const keysToExpand = new Set<React.Key>(expandedKeys);

        matchingCategoryIds.forEach(categoryId =>
        {
            let currentParentId = parentIdByCategoryId.get(categoryId) ?? null;

            while (currentParentId)
            {
                keysToExpand.add(toTreeKey(currentParentId));
                currentParentId = parentIdByCategoryId.get(currentParentId) ?? null;
            }
        });

        setExpandedKeys(Array.from(keysToExpand));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchPhrase, matchingCategoryIds, parentIdByCategoryId]);

    const checkedCategoryIdSet = useMemo
    (
        () => new Set(normalizeCategoryIds(checkedKeys.map(toCategoryId))),
        [checkedKeys]
    );

    const treeData = useMemo(() =>
    {
        const nodeByCategoryId = new Map<number, any>();
        const roots: any[] = [];

        categories.forEach(category =>
        {
            const name = category.name?.trim() || `#${category.categoryId}`;
            const isMatched = matchingCategoryIds.has(category.categoryId);
            const isChecked = checkedCategoryIdSet.has(category.categoryId);

            nodeByCategoryId.set(category.categoryId, {
                key: toTreeKey(category.categoryId),
                title: (
                    <span className="d-flex align-items-center w-100">
                        <span className="flex-grow-1">
                            <strong>{highlightText(name, searchPhrase)}</strong>
                            <span className="text-muted ml-2 small">
                                #{category.categoryId}
                            </span>
                            {category.path && category.path !== name && (
                                <span className="text-muted ml-2 small">
                                    {highlightText(category.path, searchPhrase)}
                                </span>
                            )}
                        </span>
                        <span className="ml-2 small text-muted">
                            {category.directProductCount ?? 0} prod.
                        </span>
                        {isChecked && <Badge color="danger" className="ml-2">wykluczona</Badge>}
                        {!isChecked && isMatched && <Badge color="info" className="ml-2">wynik</Badge>}
                    </span>
                ),
                children: []
            });
        });

        categories.forEach(category =>
        {
            const node = nodeByCategoryId.get(category.categoryId);

            if (!node)
            {
                return;
            }

            if (category.parentCategoryId && nodeByCategoryId.has(category.parentCategoryId))
            {
                nodeByCategoryId.get(category.parentCategoryId).children.push(node);
            }
            else
            {
                roots.push(node);
            }
        });

        const sortNodes = (nodes: any[]) =>
        {
            nodes.sort((left, right) =>
            {
                const leftCategory = categoriesById.get(toCategoryId(left.key));
                const rightCategory = categoriesById.get(toCategoryId(right.key));

                return (leftCategory?.path ?? '').localeCompare(rightCategory?.path ?? '', 'pl') ||
                    toCategoryId(left.key) - toCategoryId(right.key);
            });

            nodes.forEach(node => sortNodes(node.children));
        };

        sortNodes(roots);

        return roots;
    }, [categories, categoriesById, checkedCategoryIdSet, matchingCategoryIds, searchPhrase]);

    const categoryIdsToExclude = useMemo(() =>
    {
        const initialSet = new Set(initialExcludedCategoryIds);
        return normalizeCategoryIds(Array.from(checkedCategoryIdSet).filter(categoryId => !initialSet.has(categoryId)));
    }, [checkedCategoryIdSet, initialExcludedCategoryIds]);

    const categoryIdsToInclude = useMemo(() =>
    {
        return normalizeCategoryIds(initialExcludedCategoryIds.filter(categoryId => !checkedCategoryIdSet.has(categoryId)));
    }, [checkedCategoryIdSet, initialExcludedCategoryIds]);

    const expandAll = () =>
    {
        setExpandedKeys(categories.map(category => toTreeKey(category.categoryId)));
    };

    const collapseAll = () =>
    {
        setExpandedKeys([]);
    };

    const save = async () =>
    {
        setLastError(null);
        setLastMessage(null);
        setIsSaving(true);

        try
        {
            const excludedCategoryIds = normalizeCategoryIds(Array.from(checkedCategoryIdSet));
            const result = await updateCategoryExclusions({
                excludedCategoryIds,
                categoryIdsToExclude,
                categoryIdsToInclude,
                excludeDescendants: false,
                markVisibleProductsDirty: true
            });

            const payload = unwrapPayload(result);
            const effectiveExcludedCategoryIds = getEffectiveExcludedCategoryIds(payload);

            setData(payload);
            setInitialExcludedCategoryIds(effectiveExcludedCategoryIds);
            setCheckedKeys(effectiveExcludedCategoryIds.map(toTreeKey));
            setLastMessage(payload?.queueResult?.message ?? payload?.message ?? 'Zapisano wykluczenia kategorii.');
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Zapis wykluczeń kategorii zakończył się błędem.');
        }
        finally
        {
            setIsSaving(false);
        }
    };

    return (
        <Container fluid>
            {lastError && <Alert color="danger">{lastError}</Alert>}
            {lastMessage && <Alert color="success">{lastMessage}</Alert>}

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
                <div>
                    <h4 className="mb-2">Wykluczenia kategorii</h4>
                    <p className="text-muted mb-2">
                        Zaznacz kategorię, aby wykluczyć ją z eksportu Google Merchant. Drzewo obsługuje rozwijanie,
                        zwijanie, checkbox trójstanowy i wirtualizację listy widocznych wierszy.
                    </p>
                </div>

                <div className="d-flex flex-wrap align-items-center">
                    <Button
                        type="button"
                        color="secondary"
                        className="mr-2 mb-2"
                        disabled={isLoading || isSaving}
                        onClick={loadData}
                    >
                        Odśwież
                    </Button>
                    <Button
                        type="button"
                        color="primary"
                        className="mb-2"
                        disabled={isLoading || isSaving}
                        onClick={save}
                    >
                        Zapisz wykluczenia
                    </Button>
                </div>
            </div>

            <div className="d-flex flex-wrap align-items-center mb-3">
                <Input
                    type="text"
                    className="mr-2 mb-2"
                    style={{ maxWidth: 420 }}
                    placeholder="Szukaj po nazwie, ścieżce albo ID kategorii"
                    value={searchPhrase}
                    onChange={event => setSearchPhrase(event.target.value)}
                />
                <Button type="button" color="secondary" outline className="mr-2 mb-2" onClick={expandAll}>
                    Rozwiń wszystko
                </Button>
                <Button type="button" color="secondary" outline className="mb-2" onClick={collapseAll}>
                    Zwiń wszystko
                </Button>
            </div>

            <Alert color="info">
                Wykluczonych efektywnie: <strong>{checkedCategoryIdSet.size}</strong>.
                Do dodania: <strong>{categoryIdsToExclude.length}</strong>, do przywrócenia: <strong>{categoryIdsToInclude.length}</strong>.
                Po zapisie aplikacja oznaczy widoczne produkty do ponownego sprawdzenia.
            </Alert>

            <div className="border rounded bg-white p-2">
                {isLoading && <div className="text-center text-muted py-4">Ładowanie kategorii...</div>}

                {!isLoading && treeData.length > 0 && (
                    <Tree
                        checkable
                        selectable={false}
                        treeData={treeData}
                        checkedKeys={checkedKeys}
                        expandedKeys={expandedKeys}
                        height={560}
                        itemHeight={34}
                        virtual
                        onExpand={(nextExpandedKeys: React.Key[]) => setExpandedKeys(nextExpandedKeys)}
                        onCheck={(nextCheckedKeys: any) =>
                        {
                            const keys = Array.isArray(nextCheckedKeys)
                                ? nextCheckedKeys
                                : nextCheckedKeys?.checked ?? [];

                            setCheckedKeys(keys);
                        }}
                    />
                )}

                {!isLoading && treeData.length === 0 && (
                    <div className="text-center text-muted py-4">
                        Brak kategorii do wyświetlenia.
                    </div>
                )}
            </div>
        </Container>
    );
}

export default ExclusionsSection;
